/**
 * Knowledge manifest schema and validation.
 *
 * Moved from @skaile/workspaces/types/manifests/knowledge.ts in Phase 1.7.4.
 * This is now the canonical location for knowledge manifest validation.
 *
 * Knowledge is currently a read-only slot (runtime support deferred to Phase 4).
 * The schema is minimal: name, description, version, files, keywords, license.
 */

import * as z from "zod";

// ---------------------------------------------------------------------------
// Validation result (inline — avoids importing from types/manifests/_shared)
// ---------------------------------------------------------------------------

export interface ManifestValidationSuccess<T> {
  ok: true;
  data: T;
}

export interface ManifestValidationFailure {
  ok: false;
  errors: Array<{ path: string; message: string }>;
}

export type ManifestValidationResult<T> = ManifestValidationSuccess<T> | ManifestValidationFailure;

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/**
 * Knowledge manifest schema.
 * Slot reserved per spec — runtime support deferred to Phase 4.
 * Currently just an index of files.
 */
export const KnowledgeManifestSchema = z.looseObject({
  name: z.string().optional(),
  description: z.string().optional(),
  version: z.string().optional(),
  files: z.array(z.string()).optional(),
  keywords: z.array(z.string()).optional(),
  license: z.string().optional(),
});

export type KnowledgeManifest = z.infer<typeof KnowledgeManifestSchema>;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function validateKnowledge(data: unknown): ManifestValidationResult<KnowledgeManifest> {
  const result = KnowledgeManifestSchema.safeParse(data);
  if (result.success) {
    return { ok: true, data: result.data };
  }
  return {
    ok: false,
    errors: result.error.issues.map((issue) => ({
      path: issue.path.join("."),
      message: issue.message,
    })),
  };
}
